import {
    collection,
    doc,
    getDocs,
    getDoc,
    addDoc,
    updateDoc,
    deleteDoc,
    query,
    where,
    orderBy,
    serverTimestamp
} from 'firebase/firestore';
import { db } from './config';
import { getErrorMessage } from '../utils/errorUtils';
import {
    SIZE_KEY_TO_DISPLAY as _SIZE_KEY_TO_DISPLAY,
    SIZE_DISPLAY_TO_KEY as _SIZE_DISPLAY_TO_KEY,
    normalizeSizePricesToDisplayKeys,
} from '../utils/sizeUtils';

/**
 * Product data structure:
 * {
 *   id: string (Firestore document ID),
 *   name: string,
 *   price: number,
 *   category: string,
 *   subcategory?: string,
 *   sizeTier?: 'Adults' | 'Kids' | null,
 *   colors?: string[],
 *   colorPrices?: { [color: string]: number },
 *   inventory?: { [color: string]: { [size: string]: number } },
 *   stockQuantity?: number,
 *   imageUrl?: string,
 *   images?: string[],
 *   status: 'active' | 'inactive',
 *
 *   // T-Shirt / Jersey / Polo specific fields
 *   // NOTE: sizePrices keys are stored as DISPLAY NAMES ('Small', '2XL', etc.)
 *   sizePrices?: { [displaySizeKey: string]: number | null },
 *   availableSizes?: string[],
 *   // selectedAttributes supports per-value pricing
 *   selectedAttributes?: {
 *     [attributeName: string]: { value: string, price?: number } | Array<{ value: string, price?: number }>
 *   },
 *   // Explicit submenu membership tags — used by ProductCatalog for tag-based filtering
 *   submenuTags?: string[],
 * }
 */

const REQUIRED_FIELDS = ['name', 'price', 'category', 'status'];
const SIZE_TIERS  = ['Adults', 'Kids'];
const ADULT_SIZES = ['XXS', 'XS', 'Small', 'Medium', 'Large', 'XL', '2XL', '3XL', '4XL', '5XL', '6XL'];
const KIDS_SIZES  = ['XS', 'Small', 'Medium', 'Large', 'XL', '2XL'];
const SIZE_MAP    = { Adults: ADULT_SIZES, Kids: KIDS_SIZES };

// ─── Re-export canonical size maps from sizeUtils ────────────────────────────
export const SIZE_KEY_TO_DISPLAY = _SIZE_KEY_TO_DISPLAY;
export const SIZE_DISPLAY_TO_KEY = _SIZE_DISPLAY_TO_KEY;

// ─── Fallback constants (used as initial state while Firebase fetches) ────────
export const FALLBACK_SHIRT_SIZE_PRICES = {};
export const FALLBACK_TSHIRT_PRODUCTS   = [];

export function getStockForSize(colorInventory, lowercaseSizeKey) {
    if (!colorInventory) return null;
    const displayKey = SIZE_KEY_TO_DISPLAY[lowercaseSizeKey];
    if (!displayKey) return null;
    const val = colorInventory[displayKey];
    return typeof val === 'number' ? val : null;
}

export function getTotalStockForColor(inventory, colorName) {
    if (!inventory || !colorName) return null;
    const colorStock = inventory[colorName];
    if (!colorStock) return null;
    if (typeof colorStock === 'number') return colorStock;
    return Object.values(colorStock).reduce((s, v) => s + (v || 0), 0);
}

function isFlatAttributeInventory(inventory) {
    if (!inventory || typeof inventory !== 'object' || Array.isArray(inventory)) return false;
    const entries = Object.entries(inventory).filter(([key]) => key !== '_nocolor');
    if (entries.length === 0) return false;
    return entries.every(([, qty]) => typeof qty === 'number' && Number.isFinite(qty));
}

export function getTotalStock(inventory) {
    if (!inventory) return null;

    if (inventory._nocolor && typeof inventory._nocolor === 'object' && !Array.isArray(inventory._nocolor)) {
        return Object.values(inventory._nocolor).reduce((s, v) => s + (Number(v) || 0), 0);
    }

    if (isFlatAttributeInventory(inventory)) {
        return Object.values(inventory).reduce((s, v) => s + (Number(v) || 0), 0);
    }

    return Object.values(inventory).reduce(
        (total, colorStock) => {
            if (typeof colorStock === 'number') return total + (colorStock || 0);
            if (!colorStock || typeof colorStock !== 'object') return total;
            return total + Object.values(colorStock).reduce((s, v) => s + (v || 0), 0);
        },
        0
    );
}

/** True when product has sellable stock (active products with zero stock return false). */
export function isProductInStock(product) {
    if (!product || product.status === 'inactive') return false;
    if (product.inventory && typeof product.inventory === 'object' && Object.keys(product.inventory).length > 0) {
        const total = getTotalStock(product.inventory);
        return total !== null && total > 0;
    }
    if (typeof product.stockQuantity === 'number') {
        return product.stockQuantity > 0;
    }
    return true;
}

// ─── getProductsBySubcategory ─────────────────────────────────────────────────
/**
 * Generic fetcher for any subcategory (T-Shirt, Jersey, Polo Shirt, etc.)
 *
 * @param {string} subcategory - e.g. 'T-Shirt', 'Jersey', 'Polo Shirt'
 * @param {Object} options
 * @param {boolean} options.includeInactive - include inactive products if true
 *
 * @returns {Promise<{ products: Array, fromCache: boolean, error: string|null }>}
 */
export async function getProductsBySubcategory(subcategory, { includeInactive = false, includeOutOfStock = false } = {}) {
    try {
        let q;
        if (includeInactive) {
            q = query(
                collection(db, 'products'),
                where('subcategory', '==', subcategory)
            );
        } else {
            q = query(
                collection(db, 'products'),
                where('subcategory', '==', subcategory),
                where('status', '==', 'active')
            );
        }

        const snapshot = await getDocs(q);

        if (snapshot.empty) {
            return { products: [], fromCache: false, error: null };
        }

        const products = [];

        snapshot.forEach(docSnap => {
            const data = docSnap.data();
            products.push({
                id:             docSnap.id,
                name:           data.name            || '',
                price:          data.price           || 0,
                startingPrice:  data.price           || 0,
                sizePrices:     data.sizePrices      || null,
                availableSizes: data.availableSizes  || null,
                imageUrl:       data.imageUrl        || data.image || '',
                images:         Array.isArray(data.images) ? data.images.filter(Boolean) : [],
                status:         data.status          || 'active',
                category:       data.category        || 'Clothing',
                subcategory:    data.subcategory     || subcategory,
                sizeTier:       data.sizeTier        || null,
                colors:         Array.isArray(data.colors) ? data.colors : [],
                colorPrices:    data.colorPrices && typeof data.colorPrices === 'object'
                                    ? data.colorPrices : {},
                inventory:      data.inventory && typeof data.inventory === 'object'
                                    ? data.inventory : null,
                stockQuantity:  data.stockQuantity   ?? 0,
                description:    data.description     || '',
                submenuTags:    Array.isArray(data.submenuTags) ? data.submenuTags : [],
                selectedAttributes: data.selectedAttributes && typeof data.selectedAttributes === 'object'
                                    ? data.selectedAttributes : null,
            });
        });

        products.sort((a, b) => a.name.localeCompare(b.name));

        const visibleProducts = includeOutOfStock
            ? products
            : products.filter(isProductInStock);

        console.debug('[products:getProductsBySubcategory] fetched products', {
            subcategory,
            count: visibleProducts.length,
            includeInactive,
            includeOutOfStock,
            descriptionCount: visibleProducts.filter(product => Boolean(product.description)).length,
        });

        return { products: visibleProducts, fromCache: false, error: null };

    } catch (err) {
        console.warn(`getProductsBySubcategory(${subcategory}): Firebase fetch failed.`, err);
        return { products: [], fromCache: false, error: getErrorMessage(err, 'Failed to fetch products. Please try again.') };
    }
}

// ─── getTShirtProducts ────────────────────────────────────────────────────────
/**
 * Kept for backward-compatibility with ProductDetails and other components
 * that still import getTShirtProducts directly.
 * Internally delegates to getProductsBySubcategory.
 */
export async function getTShirtProducts({ includeInactive = false } = {}) {
    const { products, fromCache, error } = await getProductsBySubcategory('T-Shirt', { includeInactive });
    // Build sizePrices map from product data (dynamic, no hardcoded fallback)
    const sizePrices = {};
    products.forEach(p => {
        if (p.name && p.sizePrices && typeof p.sizePrices === 'object') {
            sizePrices[p.name] = p.sizePrices;
        }
    });
    return { products, sizePrices, fromCache, error };
}

// ─── getShirtSizePrices ───────────────────────────────────────────────────────
export async function getShirtSizePrices() {
    try {
        const q = query(
            collection(db, 'products'),
            where('subcategory', '==', 'T-Shirt'),
            where('status', '==', 'active')
        );
        const snapshot = await getDocs(q);

        if (snapshot.empty) {
            return { sizePrices: {}, fromCache: false };
        }

        const sizePrices = {};
        snapshot.forEach(docSnap => {
            const data = docSnap.data();
            if (data.name && data.sizePrices && typeof data.sizePrices === 'object') {
                sizePrices[data.name] = data.sizePrices;
            }
        });

        return { sizePrices, fromCache: false };

    } catch (err) {
        console.warn('getShirtSizePrices: Firebase fetch failed.', err);
        return { sizePrices: {}, fromCache: false, error: getErrorMessage(err, 'Failed to fetch size prices. Please try again.') };
    }
}

// ─── Normalizer helpers ───────────────────────────────────────────────────────
function normalizeString(value) {
    return typeof value === 'string' ? value.trim() : '';
}

function normalizeStringArray(values) {
    if (!Array.isArray(values)) return [];
    const seen = new Set();
    const cleaned = [];
    values.forEach((value) => {
        const v = normalizeString(value);
        if (!v || seen.has(v.toLowerCase())) return;
        seen.add(v.toLowerCase());
        cleaned.push(v);
    });
    return cleaned;
}

function normalizeSizeTier(value, category) {
    if (value === undefined) return { sizeTier: null, error: null };
    if (value === null || value === '') return { sizeTier: null, error: null };
    if (!SIZE_TIERS.includes(value)) return { sizeTier: null, error: 'Size tier must be either Adults or Kids.' };
    // Only reject if a non-null size tier is provided for a non-Clothing category
    if (value && category && typeof category === 'string' && category.trim() !== 'Clothing') {
        return { sizeTier: null, error: 'Size tier is only allowed for Clothing products.' };
    }
    return { sizeTier: value, error: null };
}

function isClothingSizeTierRequired(data = {}) {
    const category = normalizeString(data.category);
    if (category !== 'Clothing') return false;

    const attrs = data.selectedAttributes || data.attributes;
    if (attrs && typeof attrs === 'object' && !Array.isArray(attrs) && Object.keys(attrs).length > 0) {
        return false;
    }

    if (data.sizePrices && typeof data.sizePrices === 'object' && Object.keys(data.sizePrices).length > 0) return true;
    if (Array.isArray(data.availableSizes) && data.availableSizes.length > 0) return true;

    if (data.inventory && typeof data.inventory === 'object' && !Array.isArray(data.inventory) && Object.keys(data.inventory).length > 0) {
        if (data.inventory._nocolor) return true;
        if (isFlatAttributeInventory(data.inventory)) return false;
        return false;
    }

    return false;
}

function isSimpleColorInventory(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
    const colors = Object.entries(value).filter(([key]) => key !== '_nocolor');
    if (colors.length === 0) return false;
    return colors.every(([, stockBySize]) => {
        if (!stockBySize || typeof stockBySize !== 'object' || Array.isArray(stockBySize)) return false;
        const keys = Object.keys(stockBySize);
        return keys.length > 0 && keys.every(k => k === 'Qty');
    });
}

function normalizeInventory(value, sizeTier) {
    if (value === undefined) return { inventory: undefined, colors: undefined, stockQuantity: undefined, error: null };
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return { inventory: null, colors: null, stockQuantity: null, error: 'Inventory must be an object keyed by color.' };
    }

    // Allow _nocolor key for no-color products (Polo Shirt, Jersey) without requiring a size tier
    const noColorData = value['_nocolor'];
    if (noColorData && typeof noColorData === 'object' && !Array.isArray(noColorData)) {
        const stock = {};
        let total = 0;
        Object.entries(noColorData).forEach(([rawSize, rawQty]) => {
            const size = normalizeString(rawSize);
            if (!size) return;
            const qty = Number(rawQty);
            if (!Number.isFinite(qty) || qty < 0) return;
            stock[size] = Math.floor(qty);
            total += Math.floor(qty);
        });
        // Even if no valid size entries, keep the key so it's preserved in Firestore
        return {
            inventory: { '_nocolor': Object.keys(stock).length > 0 ? stock : noColorData },
            colors: [],
            stockQuantity: total,
            error: null,
        };
    }

    // Flat key-value inventory for dynamic attribute products (e.g. "White:Small": 5)
    if (isFlatAttributeInventory(value)) {
        const inventory = {};
        let total = 0;
        Object.entries(value).forEach(([rawKey, rawQty]) => {
            const key = normalizeString(rawKey);
            if (!key) return;
            const qty = Number(rawQty);
            if (!Number.isFinite(qty) || qty < 0) return;
            inventory[key] = Math.floor(qty);
            total += Math.floor(qty);
        });
        return { inventory, colors: undefined, stockQuantity: total, error: null };
    }

    if (!sizeTier || !SIZE_MAP[sizeTier]) {
        if (!isSimpleColorInventory(value)) {
            return { inventory: null, colors: null, stockQuantity: null, error: 'Inventory requires a valid size tier (Adults or Kids).' };
        }

        const inventory = {};
        const colors = [];
        let total = 0;

        Object.entries(value).forEach(([rawColor, stockBySize]) => {
            const color = normalizeString(rawColor);
            if (!color) return;
            if (!stockBySize || typeof stockBySize !== 'object' || Array.isArray(stockBySize)) return;
            const colorStock = {};
            Object.entries(stockBySize).forEach(([rawSize, rawQty]) => {
                const size = normalizeString(rawSize);
                if (!size) return;
                const qty = Number(rawQty);
                if (!Number.isFinite(qty) || qty < 0) return;
                colorStock[size] = Math.floor(qty);
                total += Math.floor(qty);
            });
            if (Object.keys(colorStock).length > 0) {
                inventory[color] = colorStock;
                colors.push(color);
            }
        });

        return { inventory, colors, stockQuantity: total, error: null };
    }

    const allowedSizes = new Set(SIZE_MAP[sizeTier]);
    const inventory = {};
    const colors = [];
    let total = 0;

    Object.entries(value).forEach(([rawColor, stockBySize]) => {
        const color = normalizeString(rawColor);
        if (!color) return;
        if (!stockBySize || typeof stockBySize !== 'object' || Array.isArray(stockBySize)) return;
        const colorStock = {};
        Object.entries(stockBySize).forEach(([rawSize, rawQty]) => {
            const size = normalizeString(rawSize);
            if (!allowedSizes.has(size)) return;
            const qty = Number(rawQty);
            if (!Number.isFinite(qty) || qty < 0) return;
            colorStock[size] = Math.floor(qty);
            total += Math.floor(qty);
        });
        if (Object.keys(colorStock).length > 0) {
            inventory[color] = colorStock;
            colors.push(color);
        }
    });

    return { inventory, colors, stockQuantity: total, error: null };
}

function removeUndefinedFields(payload) {
    Object.keys(payload).forEach((key) => {
        if (payload[key] === undefined) delete payload[key];
    });
    return payload;
}

function normalizeSelectedAttributes(attrs) {
    if (!attrs || typeof attrs !== 'object' || Array.isArray(attrs)) return undefined;
    const result = {};
    for (const [attrName, attrData] of Object.entries(attrs)) {
        if (Array.isArray(attrData)) {
            result[attrName] = attrData.map(item => {
                if (typeof item === 'object' && item !== null) {
                    return { value: String(item.value || ''), price: Number(item.price) || 0 };
                }
                return { value: String(item), price: 0 };
            });
        } else if (typeof attrData === 'object' && attrData !== null) {
            result[attrName] = { value: String(attrData.value || ''), price: Number(attrData.price) || 0 };
        } else {
            result[attrName] = { value: String(attrData), price: 0 };
        }
    }
    return result;
}

function normalizeCreatePayload(data = {}) {
    const category = normalizeString(data.category);
    const { sizeTier, error: sizeTierError } = normalizeSizeTier(data.sizeTier, category);
    if (sizeTierError) return { payload: null, error: sizeTierError };

    const inventoryResult = normalizeInventory(data.inventory, sizeTier);
    if (inventoryResult.error) return { payload: null, error: inventoryResult.error };

    const imageUrl = normalizeString(data.imageUrl || data.image);
    const images   = Array.isArray(data.images) ? data.images.filter(Boolean) : [];
    const colors   = normalizeStringArray(data.colors);
    const stockQuantitySource = inventoryResult.stockQuantity !== undefined
        ? inventoryResult.stockQuantity
        : (data.stockQuantity === '' || data.stockQuantity === null ? undefined : data.stockQuantity ?? data.stock);
    const hasStockQuantity = stockQuantitySource !== undefined && stockQuantitySource !== null && stockQuantitySource !== '';
    const stockQuantity = hasStockQuantity ? Number(stockQuantitySource) : undefined;

    const payload = {
        name:           normalizeString(data.name),
        price:          Number(data.price),
        category,
        subcategory:    normalizeString(data.subcategory) || null,
        sizeTier,
        colors:         inventoryResult.colors || colors,
        inventory:      inventoryResult.inventory,
        stockQuantity,
        imageUrl,
        images,
        status:         data.status || 'active',
        // Normalize sizePrices to display-name keys on write for consistency with inventory matrix
        sizePrices:     data.sizePrices && typeof data.sizePrices === 'object'
                            ? normalizeSizePricesToDisplayKeys(data.sizePrices) : undefined,
        availableSizes: Array.isArray(data.availableSizes) ? data.availableSizes : undefined,
        colorPrices:    data.colorPrices && typeof data.colorPrices === 'object'
                            ? data.colorPrices : undefined,
        description:    normalizeString(data.description) || '',
        selectedAttributes: normalizeSelectedAttributes(data.selectedAttributes || data.attributes),
        submenuTags:    Array.isArray(data.submenuTags) ? data.submenuTags.filter(Boolean) : [],
    };

    const missing = REQUIRED_FIELDS.filter((field) => !payload[field] && payload[field] !== 0);
    if (missing.length > 0)                                    return { payload: null, error: `Missing required fields: ${missing.join(', ')}` };
    if (!Number.isFinite(payload.price) || payload.price < 0)  return { payload: null, error: 'Price must be a non-negative number.' };
    if (hasStockQuantity && (!Number.isInteger(payload.stockQuantity) || payload.stockQuantity < 0)) return { payload: null, error: 'Stock quantity must be a non-negative integer.' };
    if (!['active', 'inactive'].includes(payload.status))      return { payload: null, error: 'Status must be either active or inactive.' };
    if (payload.category === 'Clothing' && !payload.sizeTier && isClothingSizeTierRequired(data)) {
        return { payload: null, error: 'Clothing products must specify a size tier (Adults or Kids).' };
    }

    return { payload, error: null };
}

function normalizeUpdatePayload(data = {}) {
    const payload  = {};
    const category = data.category !== undefined ? normalizeString(data.category) : undefined;

    if (data.name        !== undefined) payload.name        = normalizeString(data.name);
    if (data.category    !== undefined) payload.category    = category;
    if (data.subcategory !== undefined) payload.subcategory = normalizeString(data.subcategory) || null;
    if (data.imageUrl    !== undefined || data.image !== undefined) payload.imageUrl = normalizeString(data.imageUrl || data.image);
    if (data.images      !== undefined) payload.images = Array.isArray(data.images) ? data.images.filter(Boolean) : [];
    // Normalize sizePrices to display-name keys on write for consistency with inventory matrix
    if (data.sizePrices  !== undefined && typeof data.sizePrices === 'object') payload.sizePrices = normalizeSizePricesToDisplayKeys(data.sizePrices);
    if (data.availableSizes !== undefined && Array.isArray(data.availableSizes)) payload.availableSizes = data.availableSizes;
    if (data.colorPrices !== undefined && typeof data.colorPrices === 'object') payload.colorPrices = data.colorPrices;
    if (data.description !== undefined) payload.description = normalizeString(data.description) || '';
    if (data.selectedAttributes !== undefined && typeof data.selectedAttributes === 'object') {
        payload.selectedAttributes = normalizeSelectedAttributes(data.selectedAttributes);
    } else if (data.attributes !== undefined && typeof data.attributes === 'object') {
        payload.selectedAttributes = normalizeSelectedAttributes(data.attributes);
    }
    if (data.submenuTags !== undefined) {
        payload.submenuTags = Array.isArray(data.submenuTags) ? data.submenuTags.filter(Boolean) : [];
    }

    if (data.status !== undefined) {
        if (!['active', 'inactive'].includes(data.status)) return { payload: null, error: 'Status must be either active or inactive.' };
        payload.status = data.status;
    }
    if (data.price !== undefined) {
        const price = Number(data.price);
        if (!Number.isFinite(price) || price < 0) return { payload: null, error: 'Price must be a non-negative number.' };
        payload.price = price;
    }
    if (data.stockQuantity !== undefined || data.stock !== undefined) {
        const rawStock = data.stockQuantity !== undefined ? data.stockQuantity : data.stock;
        if (rawStock === '' || rawStock === null) {
            payload.stockQuantity = undefined;
        } else {
            const stock = Number(rawStock);
            if (!Number.isInteger(stock) || stock < 0) return { payload: null, error: 'Stock quantity must be a non-negative integer.' };
            payload.stockQuantity = stock;
        }
    }
    if (data.colors !== undefined) payload.colors = normalizeStringArray(data.colors);
    if (data.sizeTier !== undefined) {
        const { sizeTier, error } = normalizeSizeTier(data.sizeTier, category);
        if (error) return { payload: null, error };
        payload.sizeTier = sizeTier;
    }
    if (data.inventory !== undefined) {
        // BUG FIX: use payload.sizeTier first (from this update), then fall back to data.sizeTier
        // (the product's existing value in Firestore, re-sent by the caller). Without this,
        // partial updates that don't re-send sizeTier would cause normalizeInventory to reject
        // valid _nocolor inventory as "missing size tier".
        const sizeTierForInventory = payload.sizeTier ?? data.sizeTier;
        const inventoryResult = normalizeInventory(data.inventory, sizeTierForInventory);
        if (inventoryResult.error) return { payload: null, error: inventoryResult.error };
        payload.inventory     = inventoryResult.inventory;
        payload.stockQuantity = inventoryResult.stockQuantity;
        if (inventoryResult.colors) payload.colors = inventoryResult.colors;
    }

    return { payload, error: null };
}

// ─── getAllProducts ───────────────────────────────────────────────────────────
export async function getAllProducts(options = {}) {
    try {
        const productsRef = collection(db, 'products');
        let q = query(productsRef);
        if (options.status)   q = query(q, where('status',   '==', options.status));
        if (options.category) q = query(q, where('category', '==', options.category));
        const hasMultipleFilters = (options.status ? 1 : 0) + (options.category ? 1 : 0) > 1;
        if (!hasMultipleFilters) q = query(q, orderBy('name'));
        const querySnapshot = await getDocs(q);
        const products = [];
        querySnapshot.forEach((doc) => { products.push({ id: doc.id, ...doc.data() }); });
        if (hasMultipleFilters) {
            products.sort((a, b) => (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase()));
        }
        console.debug('[products:getAllProducts] fetched products', {
            count: products.length,
            descriptionCount: products.filter(product => Boolean(product.description)).length,
            options,
        });
        return { products, error: null };
    } catch (error) {
        console.error('Error fetching products:', error);
        if (error.message && error.message.includes('index')) {
            return {
                products: [],
                error: getErrorMessage(error, `The query requires an index. ${error.message.includes('create it here') ? error.message.split('create it here')[1] : 'Please create the required composite index in Firebase Console.'}`)
            };
        }
        return { products: [], error: getErrorMessage(error, 'Failed to fetch products. Please try again.') };
    }
}

// ─── getProductById ───────────────────────────────────────────────────────────
export async function getProductById(productId) {
    try {
        const productRef  = doc(db, 'products', productId);
        const productSnap = await getDoc(productRef);
        if (productSnap.exists()) {
            const product = { id: productSnap.id, ...productSnap.data() };
            console.debug('[products:getProductById] fetched product', {
                productId,
                hasDescription: Boolean(product.description),
                descriptionLength: product.description?.length || 0,
            });
            return { product, error: null };
        }
        return { product: null, error: 'Product not found' };
    } catch (error) {
        console.error('Error fetching product:', error);
        return { product: null, error: getErrorMessage(error, 'Failed to fetch product. Please try again.') };
    }
}

// ─── createProduct ────────────────────────────────────────────────────────────
export async function createProduct(productData) {
    const { payload, error } = normalizeCreatePayload(productData);
    if (error) return { productId: null, error };
    try {
        const cleanedPayload = removeUndefinedFields({ ...payload });
        console.debug('[products:createProduct] writing product payload', {
            name: cleanedPayload.name,
            hasDescription: Boolean(cleanedPayload.description),
            descriptionLength: cleanedPayload.description?.length || 0,
        });
        const docRef = await addDoc(collection(db, 'products'), {
            ...cleanedPayload,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
        });
        return { productId: docRef.id, error: null };
    } catch (err) {
        console.error('Error creating product:', err);
        return { productId: null, error: getErrorMessage(err, 'Failed to create product. Please try again.') };
    }
}

// ─── updateProduct ────────────────────────────────────────────────────────────
export async function updateProduct(productId, updates) {
    const { payload, error } = normalizeUpdatePayload(updates);
    if (error) return { success: false, error };
    try {
        const cleanedPayload = removeUndefinedFields({ ...payload });
        console.debug('[products:updateProduct] writing product update', {
            productId,
            fields: Object.keys(cleanedPayload),
            hasDescriptionField: Object.prototype.hasOwnProperty.call(cleanedPayload, 'description'),
            descriptionLength: cleanedPayload.description?.length || 0,
        });
        await updateDoc(doc(db, 'products', productId), { ...cleanedPayload, updatedAt: serverTimestamp() });
        return { success: true, error: null };
    } catch (err) {
        console.error('Error updating product:', err);
        return { success: false, error: getErrorMessage(err, 'Failed to update product. Please try again.') };
    }
}

// ─── deleteProduct ────────────────────────────────────────────────────────────
export async function deleteProduct(productId) {
    try {
        await deleteDoc(doc(db, 'products', productId));
        return { success: true, error: null };
    } catch (err) {
        console.error('Error deleting product:', err);
        return { success: false, error: getErrorMessage(err, 'Failed to delete product. Please try again.') };
    }
}
